import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../ui/card";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { Input } from "../../ui/input";
import { InputFieldset } from "ui/inputFieldset";
import { SelectFieldset } from "ui/selectFieldset";
import { SelectTriggerUI } from "ui/selectTrigger";
import { TextareaUI } from "ui/textareaUI";
import { Label } from "../../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "ui/command";

import { Popover, PopoverContent, PopoverTrigger } from "ui/popover";

import { Check, ChevronsUpDown } from "lucide-react";
import {
  Plus,
  Edit2,
  Trash2,
  Search,
  Users,
  Filter,
  X,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
} from "lucide-react";
import { toast } from "sonner";
import {
  getDepartments,
  DepartmentApi,
  createDepartment,
  updateDepartment,
  deleteDepartment,
  getEmployees,
  EmployeeMasterApi,
} from "services/departmentService";
import { useScreenPermission } from "hooks/useScreenPermission";
import Pagination from "ui/pagination";
import { FullScreenLoading } from "components/ui/loader";
import { usePageHeader } from "contexts/PageHeaderContext";

interface Department {
  id: string;
  publicId: string;
  code: string;
  name: string;
  description: string;
  headOfDepartment: string;
  employeeCount: number;
  status: "active" | "inactive";
}

export default function ManageDepartmentPage() {
  const { setPageHeader } = usePageHeader();
  const [searchTerm, setSearchTerm] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [editingItem, setEditingItem] = useState<Department | null>(null);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState<EmployeeMasterApi[]>([]);
  const [employeeSearch, setEmployeeSearch] = useState("");

  // Sorting state
  const [sortColumn, setSortColumn] = useState<string>("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deletingDept, setDeletingDept] = useState<Department | null>(null);

  const { screenName, icon: ScreenIcon } = useScreenPermission();

  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Filter states
  const [filters, setFilters] = useState({
    name: "",
    code: "",
    status: "all",
  });

  const [formData, setFormData] = useState({
    code: "",
    name: "",
    description: "",
    headOfDepartment: "",
    status: "active" as "active" | "inactive",
  });

  const [errors, setErrors] = useState({
    code: "",
    name: "",
    description: "",
    headOfDepartment: "",
  });

  const fetchDepartments = async () => {
    try {
      setLoading(true);

      const res = await getDepartments({
        searchText: searchTerm || null,
        pageNo,
        pageSize,
      });

      const mapped: Department[] = res.items.map((item: DepartmentApi) => ({
        id: item.id.toString(),
        publicId: item.publicId,
        code: item.departmentCode,
        name: item.departmentName,
        description: item.description,
        headOfDepartment: item.headOfDepartment,
        employeeCount: item.employeeCount,
        status: item.status ? "active" : "inactive",
      }));

      setDepartments(mapped);
      setTotalCount(res.totalCount);
    } catch (error) {
      toast.error("Failed to fetch departments");
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const res = await getEmployees();

      setEmployees(res);
    } catch (error) {
      toast.error("Failed to fetch employees");
    }
  };

  useEffect(() => {
    setPageHeader({ 
      title: screenName,
      icon: <ScreenIcon className="h-6 w-6" />,
    });

    return () => setPageHeader(null);
  }, [screenName, ScreenIcon, setPageHeader]);

  useEffect(() => {
    fetchDepartments();
    fetchEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNo, pageSize, searchTerm]);

  useEffect(() => {
    setPageNo(1);
  }, [filters, searchTerm]);

  const filteredDepartments = departments.filter((dept) => {
    const matchesName =
      !filters.name ||
      dept.name.toLowerCase().includes(filters.name.toLowerCase());

    const matchesCode =
      !filters.code ||
      dept.code.toLowerCase().includes(filters.code.toLowerCase());

    const matchesStatus =
      filters.status === "all" || dept.status === filters.status;

    return matchesName && matchesCode && matchesStatus;
  });

  // Handle sorting
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else {
        setSortColumn("");
        setSortDirection("asc");
      }
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  // Apply sorting
  const sortedDepartments = [...filteredDepartments].sort((a, b) => {
    if (!sortColumn) return 0;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let aValue: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let bValue: any;

    if (sortColumn === "name") {
      aValue = a.name;
      bValue = b.name;
    } else if (sortColumn === "code") {
      aValue = a.code;
      bValue = b.code;
    }

    if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
    if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  const handleAdd = () => {
    setEditingItem(null);
    setFormData({
      code: "",
      name: "",
      description: "",
      headOfDepartment: "",
      status: "active",
    });
    setErrors({
      code: "",
      name: "",
      description: "",
      headOfDepartment: "",
    });
    setEmployeeSearch("");
    setShowDialog(true);
  };

  const handleEdit = (dept: Department) => {
    setEditingItem(dept);
    setFormData({
      code: dept.code,
      name: dept.name,
      description: dept.description,
      headOfDepartment: dept.headOfDepartment,
      status: dept.status,
    });
    setErrors({
      code: "",
      name: "",
      description: "",
      headOfDepartment: "",
    });
    setEmployeeSearch("");
    setShowDialog(true);
  };

  const validateForm = () => {
    const newErrors = {
      code: "",
      name: "",
      description: "",
      headOfDepartment: "",
    };

    if (!formData.code.trim()) {
      newErrors.code = "Department Code is required";
    }

    if (!formData.name.trim()) {
      newErrors.name = "Department Name is required";
    }

    setErrors(newErrors);
    return Object.values(newErrors).every((error) => error === "");
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);
      const payloadBase = {
        departmentCode: formData.code,
        departmentName: formData.name,
        description: formData.description,
        headOfDepartment: formData.headOfDepartment,
        status: formData.status === "active",
      };

      if (editingItem) {
        // UPDATE
        await updateDepartment({
          ...payloadBase,
          publicId: editingItem.publicId,
          updatedBy: "1",
        });

        toast.success("Department updated successfully");
      } else {
        // CREATE
        await createDepartment({
          ...payloadBase,
          createdBy: "1",
        });

        toast.success("Department created successfully");
      }

      const res = await getDepartments({
        searchText: null,
        pageNo: 1,
        pageSize: 50,
      });

      const mapped: Department[] = res.items.map((item: DepartmentApi) => ({
        id: item.id.toString(),
        publicId: item.publicId,
        code: item.departmentCode,
        name: item.departmentName,
        description: item.description,
        headOfDepartment: item.headOfDepartment,
        employeeCount: 0,
        status: item.status ? "active" : "inactive",
      }));

      setDepartments(mapped);
      setEmployeeSearch("");
      setShowDialog(false);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const apiMessage =
        error?.response?.data?.message || "Failed to delete department type";

      if (apiMessage.toLowerCase().includes("code")) {
        setErrors((prev) => ({ ...prev, code: apiMessage }));
      } else if (apiMessage.toLowerCase().includes("name")) {
        setErrors((prev) => ({ ...prev, name: apiMessage }));
      } else {
        toast.error(apiMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (dept: Department) => {
    setDeletingDept(dept);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!deletingDept) return;

    try {
      await deleteDepartment(deletingDept.publicId, "1");

      toast.success("Department deleted successfully");

      setDepartments((prev) =>
        prev.filter((d) => d.publicId !== deletingDept.publicId),
      );

      setIsDeleteDialogOpen(false);
      setDeletingDept(null);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const apiMessage =
        error?.response?.data?.message || "Failed to delete department";

      toast.error(apiMessage);
    }
  };

  const searchedEmployees = employees.filter(
    (employee) =>
      employee.fullName?.toLowerCase().includes(employeeSearch.toLowerCase()) ||
      employee.employeeNo
        ?.toLowerCase()
        .includes(employeeSearch.toLowerCase()) ||
      employee.itsNo?.toLowerCase().includes(employeeSearch.toLowerCase()),
  );

  return (
    <>
      {loading && <FullScreenLoading />}
      <div className="space-y-6 pb-8">
        {/* Departments List View */}
        <>
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center w-full gap-4">
            <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap w-full">
              <div className="relative flex-1 min-w-0">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
  
                <Input
                  placeholder="Search departments by name, code, or head..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setPageNo(1);
                  }}
                  className="pl-10"
                />
  
                {searchTerm && (
                  <button
                    type="button"
                    onClick={async () => {
                      setSearchTerm("");
                      setPageNo(1);
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    title="Clear search"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <Button
                variant={showFilters ? "default" : "outline"}
                size="icon"
                onClick={() => setShowFilters(!showFilters)}
                style={showFilters ? { background: "#5B9BD5" } : {}}
                className={
                  showFilters
                    ? "text-white"
                    : "border-[#5B9BD5] text-[#5B9BD5] hover:bg-sky-50"
                }
                title="Toggle Advanced Filters"
              >
                <Filter className="w-5 h-5" />
              </Button>
              <Button
                onClick={handleAdd}
                style={{ background: "#5B9BD5" }}
                className="text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Department
              </Button>
            </div>
          </div>
          
          {/* Advanced Filters Section */}
          {showFilters && (
            <Card
              className="border-[#5B9BD5]/30 bg-gradient-to-br from-sky-50/50 to-blue-50/50"
              style={{ gap: "0px" }}
            >
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle
                      style={{ color: "#5B9BD5" }}
                      className="flex items-center gap-2 text-lg"
                    >
                      <Filter className="w-5 h-5" />
                      Advanced Filters
                    </CardTitle>
                    <CardDescription className="mt-1">
                      Filter departments by multiple criteria
                    </CardDescription>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowFilters(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* Department Name Filter */}
                  <div className="space-y-2">
                    <Label htmlFor="filter-name" className="text-sm">
                      Department Name
                    </Label>
                    <Input
                      id="filter-name"
                      placeholder="Search by name..."
                      value={filters.name}
                      onChange={(e) =>
                        setFilters({ ...filters, name: e.target.value })
                      }
                      className="h-9 text-sm"
                    />
                  </div>

                  {/* Code Filter */}
                  <div className="space-y-2">
                    <Label htmlFor="filter-code" className="text-sm">
                      Department Code
                    </Label>
                    <Input
                      id="filter-code"
                      placeholder="Search by code..."
                      value={filters.code}
                      onChange={(e) =>
                        setFilters({ ...filters, code: e.target.value })
                      }
                    />
                  </div>

                  {/* Status Filter */}
                  <div className="space-y-2">
                    <Label htmlFor="filter-status" className="text-sm">
                      Status
                    </Label>
                    <Select
                      value={filters.status}
                      onValueChange={(value) =>
                        setFilters({ ...filters, status: value })
                      }
                    >
                      <SelectTrigger id="filter-status" className="h-9 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="active">Active Only</SelectItem>
                        <SelectItem value="inactive">Inactive Only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Active Filters Summary & Clear Button */}
                {(filters.name || filters.code || filters.status !== "all") && (
                  <div className="flex items-center justify-between gap-3 p-3 bg-white border border-[#5B9BD5]/30 rounded-lg">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-[#5B9BD5] mb-2">
                        Active Filters:
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {filters.name && (
                          <Badge
                            variant="outline"
                            className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs"
                          >
                            Name: {filters.name}
                          </Badge>
                        )}
                        {filters.code && (
                          <Badge
                            variant="outline"
                            className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs"
                          >
                            Code: {filters.code}
                          </Badge>
                        )}
                        {filters.status !== "all" && (
                          <Badge
                            variant="outline"
                            className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs"
                          >
                            Status: {filters.status}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setFilters({
                          name: "",
                          code: "",
                          status: "all",
                        });
                      }}
                      className="text-red-600 border-red-300 hover:bg-red-50 flex-shrink-0"
                    >
                      <X className="w-4 h-4 mr-1" />
                      Clear All
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Departments Table */}
          <Card className="bg-white border-[#5B9BD5]/30" style={{ marginTop: "10px", gap: "0px" }}>
            <CardContent style={{ padding: "0px 0px 10px 0px" }}>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow style={{ backgroundColor: "#E3F2FD" }}>
                      <TableHead
                        style={{ color: "#5B9BD5" }}
                        className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                        onClick={() => handleSort("code")}
                      >
                        <div className="flex items-center gap-1">
                          Code
                          {sortColumn === "code" ? (
                            sortDirection === "asc" ? (
                              <ArrowUp className="w-3 h-3" />
                            ) : (
                              <ArrowDown className="w-3 h-3" />
                            )
                          ) : (
                            <ArrowUpDown className="w-3 h-3 opacity-30" />
                          )}
                        </div>
                      </TableHead>
                      <TableHead
                        style={{ color: "#5B9BD5" }}
                        className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                        onClick={() => handleSort("name")}
                      >
                        <div className="flex items-center gap-1">
                          Department Name
                          {sortColumn === "name" ? (
                            sortDirection === "asc" ? (
                              <ArrowUp className="w-3 h-3" />
                            ) : (
                              <ArrowDown className="w-3 h-3" />
                            )
                          ) : (
                            <ArrowUpDown className="w-3 h-3 opacity-30" />
                          )}
                        </div>
                      </TableHead>
                      <TableHead
                        style={{ color: "#5B9BD5" }}
                        className="px-4 py-3 text-xs"
                      >
                        Head of Department
                      </TableHead>
                      <TableHead
                        style={{ color: "#5B9BD5" }}
                        className="px-4 py-3 text-xs"
                      >
                        Employees
                      </TableHead>
                      <TableHead
                        style={{ color: "#5B9BD5" }}
                        className="px-4 py-3 text-xs"
                      >
                        Status
                      </TableHead>
                      <TableHead
                        style={{ color: "#5B9BD5" }}
                        className="px-4 py-3 text-xs text-right"
                      >
                        Actions
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedDepartments.length > 0 ? (
                      sortedDepartments.map((dept) => (
                        <TableRow key={dept.id} className="hover:bg-gray-50">
                          <TableCell className="px-4 py-3 text-xs">
                            <Badge className="bg-blue-100 text-blue-700 border border-blue-300 text-xs px-2">
                              {dept.code}
                            </Badge>
                          </TableCell>
                          <TableCell className="px-4 py-3 text-xs font-medium">
                            {dept.name}
                          </TableCell>
                          <TableCell className="px-4 py-3 text-xs text-gray-600">
                            {dept.headOfDepartment}
                          </TableCell>
                          <TableCell className="px-4 py-3 text-xs">
                            <div className="flex items-center gap-1">
                              <Users className="w-3 h-3 text-gray-400" />
                              {dept.employeeCount}
                            </div>
                          </TableCell>
                          <TableCell className="px-4 py-3 text-xs">
                            {dept.status === "active" ? (
                              <Badge className="bg-green-100 text-green-700 border border-green-300 text-xs px-2">
                                Active
                              </Badge>
                            ) : (
                              <Badge className="bg-gray-100 text-gray-700 border border-gray-300 text-xs px-2">
                                Inactive
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="px-4 py-3 text-xs text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEdit(dept)}
                                className="h-8 px-2 hover:bg-sky-50"
                              >
                                <Edit2 className="w-4 h-4 text-[#5B9BD5]" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(dept)}
                                className="h-8 px-2 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4 text-red-600" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={7}
                          className="text-center py-8 text-gray-500"
                        >
                          No department found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
            <Pagination
              totalItems={totalCount}
              itemsPerPage={pageSize}
              currentPage={pageNo}
              onPageChange={(page) => setPageNo(page)}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPageNo(1);
              }}
              label="departments"
            />
          </Card>

          {/* Department Dialog */}
          <Dialog open={showDialog} onOpenChange={setShowDialog}>
            <DialogContent className="max-w-lg bg-white">
              <DialogHeader>
                <DialogTitle>
                  {editingItem ? "Edit Department" : "Add New Department"}
                </DialogTitle>
                <DialogDescription>
                  {editingItem
                    ? "Update the department details below"
                    : "Fill in the details to create a new department"}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <InputFieldset
                      label="Department Code"
                      required
                      id="code"
                      value={formData.code}
                      onChange={(e) => {
                        setFormData({ ...formData, code: e.target.value });
                        if (errors.code) setErrors({ ...errors, code: "" });
                      }}
                      placeholder="e.g., IT, HR"
                      className={errors.code ? "border-red-500" : ""}
                    />
                    {errors.code && (
                      <p className="text-red-500 text-xs">{errors.code}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <SelectFieldset label="Status">
                      <Select
                        value={formData.status}
                        onValueChange={(value: "active" | "inactive") =>
                          setFormData({ ...formData, status: value })
                        }
                      >
                        <SelectTriggerUI id="status">
                          <SelectValue />
                        </SelectTriggerUI>
                        <SelectContent>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="inactive">Inactive</SelectItem>
                        </SelectContent>
                      </Select>
                    </SelectFieldset>
                  </div>
                </div>

                <div className="space-y-2">
                  <InputFieldset
                    label="Department Name"
                    required
                    id="name"
                    value={formData.name}
                    onChange={(e) => {
                      setFormData({ ...formData, name: e.target.value });
                      if (errors.name) setErrors({ ...errors, name: "" });
                    }}
                    placeholder="e.g., Information Technology"
                  />
                  {errors.name && (
                    <p className="text-red-500 text-xs">{errors.name}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <SelectFieldset label="Description">
                    <TextareaUI
                      id="description"
                      value={formData.description}
                      onChange={(e) => {
                        setFormData({ ...formData, description: e.target.value });
                      }}
                      placeholder="Brief description of the department"
                      rows={3}
                    />
                  </SelectFieldset>
                </div>

                <SelectFieldset label="Head of Department">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        className="w-full justify-between"
                      >
                        {formData.headOfDepartment || "Select employee"}

                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>

                    <PopoverContent className="w-full p-0">
                      <Command>
                        {/* SEARCH INSIDE */}
                        <CommandInput
                          value={employeeSearch}
                          onValueChange={setEmployeeSearch}
                          placeholder="Search employee..."
                          className="border-0 focus:ring-0 focus-visible:ring-0 focus-visible:outline-none shadow-none outline-none"
                        />

                        <CommandList>
                          <CommandEmpty>No employee found.</CommandEmpty>

                          <CommandGroup>
                            {searchedEmployees.length > 0 ? (
                              searchedEmployees.map((employee) => (
                                <CommandItem
                                  key={employee.id}
                                  value={`${employee.fullName} ${employee.employeeNo} ${employee.itsNo}`}
                                  onSelect={() => {
                                    setFormData({
                                      ...formData,
                                      headOfDepartment: employee.fullName,
                                    });
                                  }}
                                >
                                  <Check
                                    className={`mr-2 h-4 w-4 ${
                                      formData.headOfDepartment ===
                                      employee.fullName
                                        ? "opacity-100"
                                        : "opacity-0"
                                    }`}
                                  />

                                  {employee.fullName}
                                </CommandItem>
                              ))
                            ) : (
                              <div className="p-3 text-sm text-gray-500">
                                No employee found
                              </div>
                            )}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </SelectFieldset>
              </div>

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowDialog(false);
                    setEmployeeSearch("");
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  style={{ background: "#5B9BD5" }}
                  className="text-white"
                >
                  {editingItem ? "Update" : "Create"} Department
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Delete Department Dialog */}
          <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
            <DialogContent className="max-w-md bg-white">
              <DialogHeader>
                <DialogTitle className="text-red-600">
                  Delete Department
                </DialogTitle>
                <DialogDescription>
                  Are you sure you want to delete{" "}
                  <span className="font-semibold">"{deletingDept?.name}"</span>?
                  This action cannot be undone.
                </DialogDescription>
              </DialogHeader>

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsDeleteDialogOpen(false);
                    setDeletingDept(null);
                  }}
                >
                  Cancel
                </Button>

                <Button
                  onClick={confirmDelete}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  Delete
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      </div>
    </>
  );
}
